// ── Island pointer handlers (god mode) ──
// Islands are not draggable like map objects; they answer two gestures only:
//   • double tap  → place a new map object at that world point (opens the type picker)
//   • long press  → the admin radial menu for the island itself
// Attached once per island container so toggling God mode never rebuilds the map.
//
// Extracted verbatim from useMapAdminInteraction; the only change is closure variables
// → an explicit deps object.
import type { MutableRefObject } from 'react';
import * as PIXI from 'pixi.js';
import type { MapObject } from '../../../models/mapObject';
import type { MapSceneState } from '../mapSceneState';
import { openAdminRadialMenu, markerClientPoint, type AdminRadialMenuDeps } from './adminRadialMenu';

const ISLAND_DBLCLICK_MS = 500;
const ISLAND_DBLCLICK_DIST = 80;
// Deliberately huge: an island covers most of the screen, so an accidental long press
// while panning must not pop the menu.
const ISLAND_LONG_PRESS_MS = 10000;

export interface IslandGodHandlersDeps {
  sceneRef: MutableRefObject<MapSceneState>;
  radialMenu: AdminRadialMenuDeps;
  clearAdminSelection: () => void;
  setPendingMapObjectCreation: (value: { x: number; y: number } | null) => void;
  /** Opens the MapObjectTypeSelectionModal (useMapModalStore.openModal('typeSelection')). */
  openMapObjectTypeSelectionModal: () => void;
}

export function attachIslandGodPointerHandlers(
  deps: IslandGodHandlersDeps,
  container: PIXI.Container,
  island: MapObject,
): void {
  const {
    sceneRef,
    radialMenu,
    clearAdminSelection,
    setPendingMapObjectCreation,
    openMapObjectTypeSelectionModal,
  } = deps;

  let islandLastClickX = 0;
  let islandLastClickY = 0;
  let islandLastClickTime = 0;

  container.on('pointerdown', (event: PIXI.FederatedPointerEvent) => {
    clearAdminSelection();

    const now = Date.now();
    const tapX = event.global.x;
    const tapY = event.global.y;
    const dx = Math.abs(tapX - islandLastClickX);
    const dy = Math.abs(tapY - islandLastClickY);
    const timeDiff = now - islandLastClickTime;

    if (timeDiff < ISLAND_DBLCLICK_MS && dx < ISLAND_DBLCLICK_DIST && dy < ISLAND_DBLCLICK_DIST) {
      islandLastClickTime = 0;
      if (!sceneRef.current.pixi.viewport) return;
      const worldPos = sceneRef.current.pixi.viewport.toWorld(new PIXI.Point(event.screen.x, event.screen.y));
      setPendingMapObjectCreation({ x: worldPos.x, y: worldPos.y });
      openMapObjectTypeSelectionModal();
      return;
    }

    islandLastClickX = tapX;
    islandLastClickY = tapY;
    islandLastClickTime = now;

    const longPressTimer = window.setTimeout(() => {
      window.removeEventListener('pointerup', cancelTimer);
      const { viewport, app } = sceneRef.current.pixi;
      if (!viewport || !app) return;
      const point = markerClientPoint(viewport, app, container);
      openAdminRadialMenu(radialMenu, {
        mapObject: island,
        marker: container,
        screenX: point.x,
        screenY: point.y,
      });
      try {
        window.Telegram?.WebApp?.HapticFeedback?.impactOccurred?.('light');
      } catch {
        /* haptic optional */
      }
    }, ISLAND_LONG_PRESS_MS);

    const cancelTimer = () => {
      window.clearTimeout(longPressTimer);
      window.removeEventListener('pointerup', cancelTimer);
    };
    window.addEventListener('pointerup', cancelTimer);
  });
}
